// Portable helpers for the base StackedHorizontalBarChart component
// (@rdrive/plugin-chart-stacked-bar). Deliberately free of project SQL and of Vue
// imports. See packages/rdrive-plugin-chart-stacked-bar/README.md for the contract.

// escapeSql is NOT re-exported here any more (PLATFORM.md §6 decision 21): its one home is
// @rdrive/helper-sql, and a chart config imports it from there. Only ever apply it to
// *values* — never to SQL fragments such as a drill-down subquery or a table name.

// Turn flat query rows into the nested shape the base chart renders.
//
// Expected row shape: { id, parentId?, type?, title, status_title, color, value, sort }
// Output shape:       { name, id, parentId, type, children: [{ color, value, statusName, sort, statusCounts }] }
//
// Repeated (id, color, status_title) triples accumulate. Queries that use
// SUM(COUNT(*)) OVER (PARTITION BY ...) with a partition narrower than the
// GROUP BY emit such repeats; overwriting instead of accumulating made the
// tooltip counts disagree with the stacked segment lengths.
export const processChartData = (rows) => {
  const groupedData = {};

  rows.forEach(row => {
    const id = row['id'];
    const title = row['title'];
    const parentId = row['parentId'] ? row['parentId'] : null;
    const type = row['type'] ? row['type'] : null;

    if (!groupedData[id]) {
      groupedData[id] = {
        name: title,
        id: id,
        parentId: parentId,
        type: type,
        children: []
      };
    }

    // Find if this color already exists in the children
    const existingColor = groupedData[id].children.find(
      child => child.color === row.color
    );

    if (existingColor) {
      existingColor.value += row.value;
      // Store the status names by color
      if (!existingColor.statusCounts) {
        existingColor.statusCounts = {};
      }
      existingColor.statusCounts[row.status_title] =
        (existingColor.statusCounts[row.status_title] || 0) + row.value;
    } else {
      groupedData[id].children.push({
        color: row.color,
        value: row.value,
        statusName: row.status_title,
        sort: row.sort,
        statusCounts: { [row.status_title]: row.value }
      });
    }
  });

  return Object.values(groupedData).map(item => ({
    ...item,
    children: item.children.sort((a, b) => a.sort - b.sort)
  }));
};

// Run a query on a DuckDB connection and return chart-ready data.
export const executeAndProcessQuery = async (conn, query) => {
  const result = await conn.query(query);
  const rows = result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      value: Number(jsonRow.value)
    };
  });

  return processChartData(rows);
};

// Closure state for "a click on the terminal status level should stay inside
// the drill-down scope the user navigated into".
//
// Two consumption strategies exist, hence two accessors:
//   andIdIn()  -> extra predicate appended to a status query   (items charts)
//   source()   -> replaces the FROM source with the subquery   (status-history category chart)
export const makeDrillScope = () => {
  let query = null;

  return {
    set: (q) => { query = q; },
    reset: () => { query = null; },
    get: () => query,
    andIdIn: (column = 'i.SubItemId') => (query ? `AND ${column} IN (${query})` : ''),
    source: (fallbackTable) => (query ? `(${query})` : fallbackTable)
  };
};
